import React, { useLayoutEffect, useRef, useState } from 'react';

const SENTENCE_BREAK = '\u0000';

/**
 * 문장 끝 뒤에서 자른다. 영어·베트남어는 마침표+공백, 중국어·일본어는 。！？ 바로 뒤.
 * 정규식 lookbehind(`(?<=`)는 쓰지 않는다 - iOS 16.3 이하 사파리가 해석하지 못해 이 파일이 든 메인 번들 전체가 죽는다.
 */
export const splitSentences = (text: string): string[] =>
  text
    .replace(/([.!?])\s+/g, `$1${SENTENCE_BREAK}`)
    .replace(/([。！？])/g, `$1${SENTENCE_BREAK}`)
    .split(SENTENCE_BREAK)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

/** 부모의 글이 들어가는 폭(패딩 제외) */
const contentWidth = (el: HTMLElement) => {
  const style = window.getComputedStyle(el);
  return el.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0');
};

/**
 * 여러 문장짜리 안내문을 문장 단위로 줄바꿈한다.
 * 한 줄에 다 들어가면 그대로 한 줄이고, 넘칠 때만 넘치는 문장을 통째로 다음 줄로 내린다 - 문장 중간에서 끊기지 않게.
 *
 * 한 줄 안에 들어가는 문장만 inline-block 으로 묶는다. 어차피 여러 줄이 되는 긴 문장까지 묶으면 그 덩어리가
 * 줄 전체 폭을 차지해, 뒤 문장이 마지막 줄의 남은 자리에 들어갈 수 있는데도 다음 줄로 밀린다(2026-10-02 대표 지적).
 * 그래서 화면 폭이 바뀔 때마다 문장별 한 줄 길이를 재서 정한다.
 */
export const SentenceText = ({ text }: { text: string }) => {
  const sentences = splitSentences(text);
  // 중국어·일본어는 문장 사이에 공백을 두지 않는다
  const joiner = /[。！？]$/.test(sentences[0] ?? '') ? '' : ' ';
  const ref = useRef<HTMLSpanElement>(null);
  const [isLong, setIsLong] = useState<boolean[]>([]);

  useLayoutEffect(() => {
    const root = ref.current;
    const parent = root?.parentElement;
    if (!root || !parent) return undefined;

    const measure = () => {
      const width = contentWidth(parent);
      if (width <= 0) return;
      const spans = Array.from(root.querySelectorAll<HTMLElement>('[data-sentence]'));
      const next = spans.map((span) => {
        const before = span.style.whiteSpace;
        span.style.whiteSpace = 'nowrap';
        const singleLine = span.getBoundingClientRect().width;
        span.style.whiteSpace = before;
        return singleLine > width;
      });
      setIsLong((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [text]);

  return (
    <span ref={ref}>
      {sentences.map((sentence, index) => (
        <React.Fragment key={index}>
          {index > 0 && joiner}
          <span data-sentence className={isLong[index] ? undefined : 'inline-block'}>
            {sentence}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
};
